import { useCallback, useMemo, useState } from "react"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

export type TUtmCampaignRow = {
  campaignName: string
  campaignId: string
  urlTags: string
  adCount: number
}

export type TUtmSummaryRow = {
  urlTags: string
  adCount: number
}

export default function useConnect() {
  const [defaultUtm, setDefaultUtm] = useState<string>(() => {
    try {
      return localStorage.getItem("utm-audit-default") || ""
    } catch {
      return ""
    }
  })

  const handleSetDefaultUtm = useCallback((utm: string) => {
    setDefaultUtm(utm)
    try {
      localStorage.setItem("utm-audit-default", utm)
    } catch {
      // Ignore storage errors
    }
  }, [])

  // Summary query — group by url_tags, count distinct ads
  const { resultSet: summaryResultSet, isLoading: isLoadingSummary } =
    useCubeQuery({
      dimensions: ["company_media_metrics_ads.url_tags"],
      measures: ["company_media_metrics_ads.ad_count"],
      timeDimensions: [
        {
          dimension: "company_media_metrics_ads.md_date",
          dateRange: "last 30 days",
        },
      ],
      filters: [
        {
          member: "company_media_metrics_ads.spend",
          operator: "gt",
          values: ["0"],
        },
        {
          member: "company_media_metrics_ads.media_channel",
          operator: "equals",
          values: ["meta"],
        },
      ],
      order: {
        "company_media_metrics_ads.ad_count": "desc",
      },
    })

  // Table query — group by campaign + url_tags
  const { resultSet: tableResultSet, isLoading: isLoadingTable } = useCubeQuery(
    {
      dimensions: [
        "company_media_metrics_ads.campaign_name",
        "company_media_metrics_ads.campaign_id",
        "company_media_metrics_ads.url_tags",
      ],
      measures: ["company_media_metrics_ads.ad_count"],
      timeDimensions: [
        {
          dimension: "company_media_metrics_ads.md_date",
          dateRange: "last 30 days",
        },
      ],
      filters: [
        {
          member: "company_media_metrics_ads.spend",
          operator: "gt",
          values: ["0"],
        },
        {
          member: "company_media_metrics_ads.media_channel",
          operator: "equals",
          values: ["meta"],
        },
      ],
      order: {
        "company_media_metrics_ads.ad_count": "desc",
      },
    },
  )

  // Detail query — get individual ad_ids for apply actions
  const { resultSet: detailResultSet, isLoading: isLoadingDetail } =
    useCubeQuery({
      dimensions: [
        "company_media_metrics_ads.ad_id",
        "company_media_metrics_ads.campaign_id",
        "company_media_metrics_ads.url_tags",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_ads.md_date",
          dateRange: "last 30 days",
        },
      ],
      filters: [
        {
          member: "company_media_metrics_ads.spend",
          operator: "gt",
          values: ["0"],
        },
        {
          member: "company_media_metrics_ads.media_channel",
          operator: "equals",
          values: ["meta"],
        },
      ],
    })

  const utmSummary: TUtmSummaryRow[] = useMemo(() => {
    if (!summaryResultSet) return []
    return (summaryResultSet.rawData() || []).map(
      (row: Record<string, unknown>) => ({
        urlTags: String(row["company_media_metrics_ads.url_tags"] ?? ""),
        adCount: Number(row["company_media_metrics_ads.ad_count"] ?? 0),
      }),
    )
  }, [summaryResultSet])

  const campaignRows: TUtmCampaignRow[] = useMemo(() => {
    if (!tableResultSet) return []
    return (tableResultSet.rawData() || []).map(
      (row: Record<string, unknown>) => ({
        campaignName: String(
          row["company_media_metrics_ads.campaign_name"] ?? "",
        ),
        campaignId: String(row["company_media_metrics_ads.campaign_id"] ?? ""),
        urlTags: String(row["company_media_metrics_ads.url_tags"] ?? ""),
        adCount: Number(row["company_media_metrics_ads.ad_count"] ?? 0),
      }),
    )
  }, [tableResultSet])

  // Build a map of (campaignId + urlTags) -> ad IDs for apply actions
  const adIdsByCampaignUtm: Record<string, string[]> = useMemo(() => {
    if (!detailResultSet) return {}
    const map: Record<string, string[]> = {}
    for (const row of detailResultSet.rawData() || []) {
      const r = row as Record<string, unknown>
      const campaignId = String(
        r["company_media_metrics_ads.campaign_id"] ?? "",
      )
      const urlTags = String(r["company_media_metrics_ads.url_tags"] ?? "")
      const adId = String(r["company_media_metrics_ads.ad_id"] ?? "")
      const key = `${campaignId}::${urlTags}`
      if (!map[key]) map[key] = []
      if (adId) map[key].push(adId)
    }
    return map
  }, [detailResultSet])

  const isLoading = isLoadingSummary || isLoadingTable || isLoadingDetail

  const getAdIdsForCampaignUtm = useCallback(
    (campaignId: string, urlTags: string): string[] =>
      adIdsByCampaignUtm[`${campaignId}::${urlTags}`] || [],
    [adIdsByCampaignUtm],
  )

  const getAllAdIdsWithoutDefaultUtm = useCallback((): string[] => {
    if (!defaultUtm) return []
    const adIds: string[] = []
    for (const [key, ids] of Object.entries(adIdsByCampaignUtm)) {
      const utmPart = key.split("::")[1]
      if (utmPart !== defaultUtm) {
        adIds.push(...ids)
      }
    }
    return adIds
  }, [adIdsByCampaignUtm, defaultUtm])

  return {
    utmSummary,
    campaignRows,
    isLoading,
    defaultUtm,
    setDefaultUtm: handleSetDefaultUtm,
    getAdIdsForCampaignUtm,
    getAllAdIdsWithoutDefaultUtm,
  }
}
